/**
 * Provider metadata aggregation + display-name lookup.
 *
 * Keeps UI surfaces from hardcoding a provider name (e.g. "ModMed") when the
 * observed context could be any provider. Derive the label from
 * `context.provider` via `providerDisplayName` instead.
 */

import type { IntegrationMetadata, IntegrationProviderId } from './types';
import { modmedIntegrationMetadata } from './modmed';
import { ecwIntegrationMetadata } from './ecw';
import { centralReachIntegrationMetadata } from './centralreach';

/**
 * Every integration's metadata, keyed by provider id. The `Record` type makes
 * this EXHAUSTIVE — adding a value to `IntegrationProviderIdSchema` won't
 * compile until its metadata is registered here, so a new EMR can never ship
 * without a display name.
 */
export const INTEGRATION_METADATA: Record<IntegrationProviderId, IntegrationMetadata> = {
  modmed: modmedIntegrationMetadata,
  ecw: ecwIntegrationMetadata,
  centralreach: centralReachIntegrationMetadata,
};

/** Human-facing display name for a provider (e.g. 'ModMed', 'eClinicalWorks'). */
export function providerDisplayName(id: IntegrationProviderId): string {
  return INTEGRATION_METADATA[id]?.name ?? id;
}
